import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useAuth } from '../../context';
import { setProfile, type Demographics } from '../../api/profile';
import { Form, FormField, SubmitButton, ErrorMessage } from '../../components/forms';
import { ActivityIndicator } from '../../components/ui';
import styles from './EditProfilePage.module.css';

const validationSchema = Yup.object().shape({
  first_name: Yup.string().nullable().label('First Name'),
  last_name: Yup.string().nullable().label('Last Name'),
  address_1: Yup.string().nullable().label('Address 1'),
  address_2: Yup.string().nullable().label('Address 2'),
  city: Yup.string().nullable().label('City'),
  state: Yup.string().nullable().label('State'),
  zip: Yup.string().nullable().label('Zip'),
  phone: Yup.string().nullable().label('Phone'),
});

interface LocationState {
  profile?: Demographics;
}

export function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginKey } = useAuth();
  const { profile } = (location.state as LocationState) || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: Demographics) => {
    if (!loginKey) return;

    setLoading(true);
    setError(null);

    const response = await setProfile(loginKey, values);
    setLoading(false);

    if (response.ok) {
      navigate('/profile');
    } else {
      setError('Failed to save profile');
    }
  };

  return (
    <div className={styles.container}>
      <ActivityIndicator visible={loading} />
      <h1 className={styles.title}>Edit Profile</h1>
      <Form<Demographics>
        initialValues={{
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          address_1: profile?.address_1 || '',
          address_2: profile?.address_2 || '',
          city: profile?.city || '',
          state: profile?.state || '',
          zip: profile?.zip || '',
          phone: profile?.phone || '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <FormField name="first_name" placeholder="First Name" autoCapitalize="words" />
        <FormField name="last_name" placeholder="Last Name" autoCapitalize="words" />
        <FormField name="address_1" placeholder="Address 1" />
        <FormField name="address_2" placeholder="Address 2" />
        <FormField name="city" placeholder="City" autoCapitalize="words" />
        <FormField name="state" placeholder="State" autoCapitalize="characters" />
        <FormField name="zip" placeholder="Zip" inputMode="numeric" />
        <FormField name="phone" placeholder="Phone" type="tel" inputMode="tel" />
        {error && <ErrorMessage error={error} visible />}
        <SubmitButton title="Save Profile" theme="secondary" />
      </Form>
      <button className={styles.cancelButton} onClick={() => navigate('/profile')}>
        Cancel
      </button>
    </div>
  );
}

export default EditProfilePage;
